import React from 'react';
import {
    Show,
    SimpleShowLayout,
    DateField,
    FunctionField,
    useTranslate,
    useGetList,
    useRecordContext,
    useRedirect,
    EditButton,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    LinearProgress,
    Alert,
    IconButton,
    Fab,
    useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material';
import {
    Info,
    Settings,
    Analytics,
    Link,
    History,
    CheckCircle,
    Cancel,
    Lock,
    PowerSettingsNew,
    ArrowBack,
    Edit as EditIcon,
} from '@mui/icons-material';

const ConfigurationDisplay = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record || !record.logic) return null;

    const config = record.logic.config || {};

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                {translate('resources.lms-templates.types.equal')} {translate('resources.lms-templates.configuration')}
            </Typography>

            <Paper sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#F5F5F5' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('resources.lms-templates.fields.powerUtilizationPercent')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <PowerSettingsNew sx={{ fontSize: 20, color: '#00C853' }} />
                                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                    {config.powerUtilizationPercent || 90}%
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('resources.lms-templates.fields.minPowerPerCharger')}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                {config.minPowerPerCharger || 6} kW
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('resources.lms-templates.fields.powerReductionStep')}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                {config.powerReductionStep || 1} kW
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('resources.lms-templates.fields.roundToNearest')}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                {config.roundToNearest || 0.5} kW
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

const UsageStatistics = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const redirect = useRedirect();

    const { data: loadGroups = [], isLoading } = useGetList('load-groups', {
        filter: { lmsTemplateId: record?.id },
        pagination: { page: 1, perPage: 10 },
        sort: { field: 'name', order: 'ASC' },
    });

    if (!record) return null;

    const totalPowerManaged = loadGroups.reduce((sum, lg) => sum + (lg.totalCapacity || 0), 0);
    const totalChargersAffected = loadGroups.reduce((sum, lg) => sum + (lg.chargerCount || 0), 0);

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                {translate('resources.lms-templates.sections.usage')}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: { xs: 1.5, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', backgroundColor: '#E8F5E8' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2rem' }, color: '#00C853' }}>
                        {record.usageCount || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                        {translate('resources.lms-templates.stats.loadGroups')}
                    </Typography>
                </Paper>

                <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', backgroundColor: '#E3F2FD' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2rem' }, color: '#1976D2' }}>
                        {totalPowerManaged.toFixed(0)} kW
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                        {translate('resources.lms-templates.stats.totalPower')}
                    </Typography>
                </Paper>

                <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', backgroundColor: '#FFF3E0' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2rem' }, color: '#EF6C00' }}>
                        {totalChargersAffected}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                        {translate('resources.lms-templates.stats.chargers')}
                    </Typography>
                </Paper>
            </Box>

            {isLoading ? (
                <LinearProgress />
            ) : loadGroups && loadGroups.length > 0 ? (
                <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        {translate('resources.lms-templates.loadGroupsUsing')}
                    </Typography>
                    <List sx={{ backgroundColor: '#FAFAFA', borderRadius: 1 }}>
                        {loadGroups.map((loadGroup) => (
                            <ListItem
                                key={loadGroup.id}
                                disablePadding
                                sx={{ borderBottom: '1px solid #E0E0E0' }}
                            >
                                <ListItemButton onClick={() => redirect(`/load-groups/${loadGroup.id}/show`)}>
                                    <ListItemText
                                        primary={loadGroup.name}
                                        secondary={`${loadGroup.totalCapacity} kW • ${loadGroup.chargerCount} ${translate('resources.chargers.name')}`}
                                    />
                                    <Link sx={{ color: '#00C853' }} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            ) : (
                <Alert severity="info">
                    {translate('resources.lms-templates.noLoadGroups')}
                </Alert>
            )}
        </Box>
    );
};

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 4 },
            gap: { xs: 1.5, sm: 2 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/lms-templates')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('ra.action.back')}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {record?.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                        {translate('resources.lms-templates.show.subtitle')}
                    </Typography>
                </Box>
            </Box>

            {!isMobile && (
                <EditButton
                    label={translate('ra.action.edit')}
                    variant="contained"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#00C853',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: '#00A843',
                            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                        },
                    }}
                />
            )}
        </Box>
    );
};

const EditFab = () => {
    const record = useRecordContext();
    const redirect = useRedirect();

    return (
        <Fab
            color="primary"
            onClick={() => redirect(`/lms-templates/${record?.id}`)}
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: '#009A75',
                '&:hover': { backgroundColor: '#007A5E' },
                zIndex: 1100,
            }}
        >
            <EditIcon />
        </Fab>
    );
};

export const LMSTemplatesShow = () => {
    const translate = useTranslate();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    return (
        <Show
            title={false}
            actions={false}
            component="div"
            sx={{
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
                    <PageHeader />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 600px' } }}>
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                        <Info sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                            {translate('resources.lms-templates.sections.basic')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.lms-templates.fields.type')}
                                            </Typography>
                                            <FunctionField
                                                render={(record: any) => (
                                                    <span style={{ pointerEvents: 'none' }}>
                                                        <Chip
                                                            label={translate(`resources.lms-templates.types.${record.type}`)}
                                                            component="div"
                                                            clickable={false}
                                                            sx={{
                                                                backgroundColor: '#00C853',
                                                                color: 'white',
                                                                cursor: 'default',
                                                            }}
                                                        />
                                                    </span>
                                                )}
                                            />
                                        </Box>

                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.lms-templates.fields.status')}
                                            </Typography>
                                            <FunctionField
                                                render={(record: any) => (
                                                    <span style={{ pointerEvents: 'none' }}>
                                                        <Chip
                                                            icon={record.isActive ? <CheckCircle /> : <Cancel />}
                                                            label={record.isActive ? translate('status.active') : translate('status.inactive')}
                                                            component="div"
                                                            clickable={false}
                                                            sx={{
                                                                backgroundColor: record.isActive ? '#E8F5E8' : '#F5F5F5',
                                                                color: record.isActive ? '#00C853' : '#757575',
                                                                cursor: 'default',
                                                            }}
                                                        />
                                                    </span>
                                                )}
                                            />
                                        </Box>
                                    </Box>

                                    <FunctionField
                                        render={(record: any) => {
                                            if (!record?.description) return null;
                                            return (
                                                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                        {translate('resources.lms-templates.fields.description')}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {record.description}
                                                    </Typography>
                                                </Box>
                                            );
                                        }}
                                    />
                                </CardContent>
                            </Card>

                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                        <Settings sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                            {translate('resources.lms-templates.sections.configuration')}
                                        </Typography>
                                    </Box>

                                    <ConfigurationDisplay />
                                </CardContent>
                            </Card>

                            <Card sx={{ overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                        <History sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                            {translate('resources.lms-templates.sections.metadata')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.lms-templates.fields.createdAt')}
                                            </Typography>
                                            <DateField source="createdAt" showTime />
                                        </Box>

                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.lms-templates.fields.updatedAt')}
                                            </Typography>
                                            <DateField source="updatedAt" showTime />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>

                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' } }}>
                            <Card sx={{ overflow: 'hidden', ...(isMobile ? {} : { position: 'sticky', top: 20 }) }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                        <Analytics sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                            {translate('resources.lms-templates.sections.analytics')}
                                        </Typography>
                                    </Box>

                                    <UsageStatistics />

                                    <FunctionField
                                        render={(record: any) => {
                                            if (!record.usageCount || record.usageCount === 0) return null;

                                            return (
                                                <Alert
                                                    severity="warning"
                                                    icon={<Lock />}
                                                    sx={{ mt: { xs: 2, sm: 3 } }}
                                                >
                                                    {translate('resources.lms-templates.warnings.inUseWarning', { count: record.usageCount })}
                                                </Alert>
                                            );
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                </Box>
                {isMobile && <EditFab />}
            </SimpleShowLayout>
        </Show>
    );
};
